import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Calendar, 
  CheckCircle, 
  Clock, 
  MapPin, 
  Star, 
  Phone, 
  Mail, 
  ArrowRight,
  Home as HomeIcon,
  Sparkles,
  Zap
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useNotification } from '../context/NotificationContext';

interface Service {
  id: string;
  name: string;
  description: string;
  price: number;
  duration: number;
}

function Home() {
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const { showNotification } = useNotification();

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const { data, error } = await supabase
          .from('services')
          .select('*')
          .order('price', { ascending: true });

        if (error) throw error;
        setServices(data || []);
      } catch (error) {
        console.error('Error fetching services:', error);
        showNotification('error', 'Failed to load services. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, [showNotification]);

  // Get the three main services for display
  const displayServices = services.slice(0, 3);

  return (
    <div className="min-h-screen bg-[#f0efe9] dark:bg-gray-900">
      {/* Hero Section */}
      <div className="relative min-h-[90vh] flex items-center">
        <div className="container mx-auto px-4 grid md:grid-cols-2 gap-12 py-16">
          <div className="flex flex-col justify-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-light text-gray-800 dark:text-white mb-6 leading-tight">
              Professional<br />Cleaning Service
            </h1>
            <div className="w-24 h-0.5 bg-gray-400 dark:bg-gray-600 mb-8"></div>
            <p className="text-lg text-gray-700 dark:text-gray-300 mb-10 max-w-lg">
              Experience the difference with our trusted, reliable, and thorough cleaning services. We provide exceptional cleaning for homes and businesses throughout Maryland, DC, and Northern Virginia.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/book"
                className="px-8 py-3 bg-gray-800 text-white dark:bg-gray-700 rounded-none hover:bg-black dark:hover:bg-gray-600 transition-colors"
              >
                Book Now
              </Link>
              <Link
                to="/services"
                className="px-8 py-3 border border-gray-800 text-gray-800 dark:border-gray-400 dark:text-gray-300 rounded-none hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                Our Services
              </Link>
            </div>
          </div>
          <div className="hidden md:flex items-center justify-center">
            <img 
              src="https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&q=80&w=600&h=600" 
              alt="Clean home interior" 
              className="object-cover w-full max-w-md h-auto"
            />
          </div>
        </div>
      </div>

      {/* Services Section */}
      <section className="py-20 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl font-light text-gray-800 dark:text-white mb-4">Our Services</h2>
            <div className="w-24 h-0.5 bg-gray-400 dark:bg-gray-600 mx-auto mb-8"></div>
            <p className="text-gray-600 dark:text-gray-300">
              We offer a range of professional cleaning services tailored to your needs
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {loading ? (
              // Loading placeholders
              [...Array(3)].map((_, index) => (
                <div key={index} className="border border-gray-200 dark:border-gray-700 p-8 flex flex-col animate-pulse">
                  <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 mb-6"></div>
                  <div className="h-6 bg-gray-200 dark:bg-gray-700 w-3/4 mb-2"></div>
                  <div className="w-12 h-0.5 bg-gray-200 dark:bg-gray-700 mb-4"></div>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 w-full mb-2"></div>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 w-5/6 mb-6"></div>
                  <div className="mt-auto">
                    <div className="h-8 bg-gray-200 dark:bg-gray-700 w-1/3 mb-6"></div>
                    <div className="space-y-2 mb-6">
                      {[...Array(3)].map((_, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <div className="w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded-full flex-shrink-0 mt-0.5"></div>
                          <div className="h-4 bg-gray-200 dark:bg-gray-700 w-full"></div>
                        </div>
                      ))}
                    </div>
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 w-1/3"></div>
                  </div>
                </div>
              ))
            ) : (
              displayServices.map((service) => (
                <div key={service.id} className="border border-gray-200 dark:border-gray-700 p-8 flex flex-col">
                  <div className="mb-6">
                    {service.name.includes('Regular') || service.name.includes('Routine') ? (
                      <HomeIcon className="w-10 h-10 text-gray-800 dark:text-gray-200" />
                    ) : service.name.includes('Deep') ? (
                      <Sparkles className="w-10 h-10 text-gray-800 dark:text-gray-200" />
                    ) : (
                      <Calendar className="w-10 h-10 text-gray-800 dark:text-gray-200" />
                    )}
                  </div>
                  <h3 className="text-xl font-medium text-gray-800 dark:text-white mb-2">{service.name}</h3>
                  <div className="w-12 h-0.5 bg-gray-400 dark:bg-gray-600 mb-4"></div>
                  <p className="text-gray-600 dark:text-gray-300 mb-6">
                    {service.description}
                  </p>
                  <div className="mt-auto">
                    <p className="text-2xl font-light text-gray-800 dark:text-white mb-6">
                      ${(service.price / 100).toFixed(2)}
                    </p>
                    <ul className="space-y-2 mb-6">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-gray-800 dark:text-gray-300 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-600 dark:text-gray-300">{service.duration} minutes service</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-gray-800 dark:text-gray-300 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-600 dark:text-gray-300">Professional equipment</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-gray-800 dark:text-gray-300 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-600 dark:text-gray-300">Satisfaction guaranteed</span>
                      </li>
                    </ul>
                    <Link 
                      to="/book" 
                      className="inline-flex items-center text-gray-800 dark:text-gray-300 hover:text-black dark:hover:text-white"
                    >
                      Book Now
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Link>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-[#f0efe9] dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl font-light text-gray-800 dark:text-white mb-4">How It Works</h2>
            <div className="w-24 h-0.5 bg-gray-400 dark:bg-gray-600 mx-auto mb-8"></div>
            <p className="text-gray-600 dark:text-gray-300">
              Booking your cleaning service is simple and hassle-free
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-gray-800 dark:bg-gray-700 text-white flex items-center justify-center mx-auto mb-6">
                <Calendar className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-medium text-gray-800 dark:text-white mb-3">Book Online</h3>
              <p className="text-gray-600 dark:text-gray-300">
                Select your service, date, and time through our easy booking system
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-gray-800 dark:bg-gray-700 text-white flex items-center justify-center mx-auto mb-6">
                <Sparkles className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-medium text-gray-800 dark:text-white mb-3">We Clean</h3>
              <p className="text-gray-600 dark:text-gray-300">
                Our professional team arrives on time and delivers exceptional service
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-gray-800 dark:bg-gray-700 text-white flex items-center justify-center mx-auto mb-6">
                <Zap className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-medium text-gray-800 dark:text-white mb-3">You Relax</h3>
              <p className="text-gray-600 dark:text-gray-300">
                Enjoy your clean, refreshed space with our satisfaction guarantee
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl font-light text-gray-800 dark:text-white mb-4">What Our Clients Say</h2>
            <div className="w-24 h-0.5 bg-gray-400 dark:bg-gray-600 mx-auto mb-8"></div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="border border-gray-200 dark:border-gray-700 p-8">
              <div className="flex items-center gap-1 mb-4">
                <Star className="w-5 h-5 text-yellow-500 fill-current" />
                <Star className="w-5 h-5 text-yellow-500 fill-current" />
                <Star className="w-5 h-5 text-yellow-500 fill-current" />
                <Star className="w-5 h-5 text-yellow-500 fill-current" />
                <Star className="w-5 h-5 text-yellow-500 fill-current" />
              </div>
              <p className="text-gray-600 dark:text-gray-300 mb-6 italic">
                "The deep cleaning service was exceptional. They paid attention to every detail and transformed my home."
              </p>
              <p className="font-medium text-gray-800 dark:text-white">Sarah T.</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Homeowner</p>
            </div>
            
            <div className="border border-gray-200 dark:border-gray-700 p-8">
              <div className="flex items-center gap-1 mb-4">
                <Star className="w-5 h-5 text-yellow-500 fill-current" />
                <Star className="w-5 h-5 text-yellow-500 fill-current" />
                <Star className="w-5 h-5 text-yellow-500 fill-current" />
                <Star className="w-5 h-5 text-yellow-500 fill-current" />
                <Star className="w-5 h-5 text-yellow-500 fill-current" />
              </div>
              <p className="text-gray-600 dark:text-gray-300 mb-6 italic">
                "Regular cleaning service for my office has been reliable and thorough. Highly recommended!"
              </p>
              <p className="font-medium text-gray-800 dark:text-white">Michael C.</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Business Owner</p>
            </div>
            
            <div className="border border-gray-200 dark:border-gray-700 p-8">
              <div className="flex items-center gap-1 mb-4">
                <Star className="w-5 h-5 text-yellow-500 fill-current" />
                <Star className="w-5 h-5 text-yellow-500 fill-current" />
                <Star className="w-5 h-5 text-yellow-500 fill-current" />
                <Star className="w-5 h-5 text-yellow-500 fill-current" />
                <Star className="w-5 h-5 text-yellow-500 fill-current" />
              </div>
              <p className="text-gray-600 dark:text-gray-300 mb-6 italic">
                "Move-out cleaning was perfect. Got my full deposit back thanks to their detailed work."
              </p>
              <p className="font-medium text-gray-800 dark:text-white">Emily R.</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Apartment Resident</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-[#f0efe9] dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl font-light text-gray-800 dark:text-white mb-4">Frequently Asked Questions</h2>
            <div className="w-24 h-0.5 bg-gray-400 dark:bg-gray-600 mx-auto mb-8"></div>
          </div>
          
          <div className="max-w-3xl mx-auto">
            {[
              {
                question: "What's included in your standard cleaning service?",
                answer: "Our standard cleaning service includes dusting, vacuuming, mopping, bathroom cleaning, kitchen cleaning, and making beds. We focus on high-traffic areas and ensure all visible surfaces are clean and sanitized."
              },
              {
                question: "How long does a typical cleaning session take?",
                answer: "A typical cleaning session can take between 2-4 hours, depending on the size of your home and the type of service you've selected. Deep cleaning services may take longer to ensure thorough attention to detail."
              },
              {
                question: "Do I need to provide cleaning supplies?",
                answer: "No, we bring all necessary cleaning supplies and equipment. However, if you prefer specific products to be used in your home, please let us know in advance."
              },
              {
                question: "Are your cleaning products eco-friendly?",
                answer: "Yes, we use environmentally friendly cleaning products that are effective yet safe for your family and pets. We can also accommodate specific product requests if you have preferences."
              }
            ].map((faq, index) => (
              <div 
                key={index}
                className="border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 mb-4"
              >
                <div className="p-6">
                  <h3 className="font-medium text-gray-800 dark:text-white mb-2">{faq.question}</h3>
                  <p className="text-gray-600 dark:text-gray-300">{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl font-light text-gray-800 dark:text-white mb-4">Contact Us</h2>
            <div className="w-24 h-0.5 bg-gray-400 dark:bg-gray-600 mx-auto mb-8"></div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12 max-w-4xl mx-auto">
            <div>
              <div className="mb-8">
                <h3 className="text-xl font-medium text-gray-800 dark:text-white mb-4">Get In Touch</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <Phone className="w-5 h-5 text-gray-800 dark:text-gray-300 mt-1" />
                    <div>
                      <p className="font-medium text-gray-800 dark:text-white">Phone</p>
                      <a href="tel:240-289-3658" className="text-gray-600 dark:text-gray-300 hover:text-black dark:hover:text-white">
                        240-289-3658
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <Mail className="w-5 h-5 text-gray-800 dark:text-gray-300 mt-1" />
                    <div>
                      <p className="font-medium text-gray-800 dark:text-white">Email</p>
                      <a href="mailto:aussicleaning@gmail.com" className="text-gray-600 dark:text-gray-300 hover:text-black dark:hover:text-white">
                        aussicleaning@gmail.com
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <MapPin className="w-5 h-5 text-gray-800 dark:text-gray-300 mt-1" />
                    <div>
                      <p className="font-medium text-gray-800 dark:text-white">Location</p>
                      <p className="text-gray-600 dark:text-gray-300">
                        Temple Hills, Maryland
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-xl font-medium text-gray-800 dark:text-white mb-4">Service Area</h3>
                <p className="text-gray-600 dark:text-gray-300 mb-6">
                  We proudly serve Maryland, DC, and Northern Virginia
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    "Temple Hills", "Washington DC", "Alexandria", "Arlington",
                    "Silver Spring", "Bethesda", "Rockville", "Fairfax"
                  ].map((area, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-gray-800 dark:text-gray-300" />
                      <span className="text-gray-600 dark:text-gray-300">{area}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="border border-gray-200 dark:border-gray-700 p-8">
              <h3 className="text-xl font-medium text-gray-800 dark:text-white mb-6">Request a Quote</h3>
              <form className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-1 focus:ring-gray-800 dark:focus:ring-gray-300"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-1 focus:ring-gray-800 dark:focus:ring-gray-300"
                  />
                </div>
                
                <div>
                  <label htmlFor="service" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Service
                  </label>
                  <select
                    id="service"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-1 focus:ring-gray-800 dark:focus:ring-gray-300"
                  >
                    <option value="">Select a service</option>
                    {services.map((service) => (
                      <option key={service.id} value={service.id}>
                        {service.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Message
                  </label>
                  <textarea
                    id="message"
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-1 focus:ring-gray-800 dark:focus:ring-gray-300"
                  ></textarea>
                </div>
                
                <button
                  type="submit"
                  className="w-full py-3 bg-gray-800 text-white dark:bg-gray-700 rounded-none hover:bg-black dark:hover:bg-gray-600 transition-colors"
                >
                  Send Message
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-800 dark:bg-gray-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-light mb-6">Ready for a Spotless Space?</h2>
          <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
            Book your cleaning service today and enjoy a pristine environment. Satisfaction guaranteed!
          </p>
          <Link
            to="/book"
            className="inline-block px-8 py-3 border-2 border-white hover:bg-white hover:text-gray-800 transition-colors"
          >
            Book Now
          </Link>
        </div>
      </section>
    </div>
  );
}

export default Home;